import { useMemo, useState, type ReactNode } from "react";
import { useAccount, useSignTypedData, usePublicClient, useWriteContract } from "wagmi";
import { parseEventLogs } from "viem";
import { Modal } from "./Modal";
import { SellMiniGrid, keyOf, type CellKey } from "./SellMiniGrid";
import { MktButton, Money, Tag } from "./primitives";
import { useMarketplaceConfig } from "./useMarketplace";
import { useValidatePartial, useCreateDemoListing, useCreateWholeParcelListing, useParcelOccupancy } from "./useSellLand";
import { fmtMicro } from "./mkt-format";
import { goMarketplace } from "./mkt-routing";
import { buildOrderTypedData } from "./order-eip712";
import { SUBDIVISION_VAULT_ABI, ERC721_APPROVAL_ABI } from "./subdivision-vault-abi";
import { marketplaceApi, MarketplaceError, type MarketplaceConfig } from "../../lib/marketplace-api";
import { useT } from "../../i18n";
import { cn } from "../cn";

export interface SellSourceParcel {
  parcel_id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  owner: string;
}

function microToExact(micro: number): string {
  return (micro / 1_000_000).toString();
}

/**
 * SellLandWizard — the SINGLE shared seller wizard (Entry A: MY MARKET → LIST
 * LAND, and Entry B: Grid Parcel Profile → SELL LAND).
 *
 * Mode is chosen by the backend-authoritative config, never duplicated:
 *  - REAL mode (preview_demo=false): authorize a REAL whole-parcel listing by
 *    signing the EIP-712 Order with the connected wallet → POST /listings.
 *    A true partial (cell-subset) sale is fail-closed here (no on-chain Vault
 *    child on Base Sepolia yet) — the future partial UX is preserved below.
 *  - PREVIEW DEMO mode: the isolated demo partial-sale flow (unchanged).
 */
export function SellLandWizard({
  open,
  sourceParcel,
  onClose,
}: {
  open: boolean;
  sourceParcel: SellSourceParcel | null;
  onClose: () => void;
}) {
  const { data: config } = useMarketplaceConfig();
  if (!sourceParcel || !config) return null;
  if (config.preview_demo) {
    return <DemoPartialWizard open={open} sourceParcel={sourceParcel} onClose={onClose} config={config} />;
  }
  return <RealSellWizard open={open} sourceParcel={sourceParcel} onClose={onClose} config={config} />;
}

// ---------------------------------------------------------------------------
// REAL mode — whole-parcel listing (EIP-712 seller order authorization)
// ---------------------------------------------------------------------------
type RealStep = "select" | "price" | "review" | "done";

/**
 * RealSellWizard — REAL-mode seller wizard (Macro #29 Stage 3, Commit 2).
 *
 * A parcel >1×1 shows an interactive area-selection mini-grid (click / drag /
 * deselect, count, geometry, backend-authoritative contiguity + 80% floor,
 * locked/ineligible cells non-selectable). A 1×1 parcel auto-selects its single
 * cell and is shown as a WHOLE parcel.
 *
 * WHOLE selection (all cells) → the canonical whole-parcel listing path
 * (seller-signed EIP-712 Order → POST /listings), unchanged.
 *
 * PARTIAL selection (contiguous subset) is validated & previewed, but FAILS
 * CLOSED before any signature/listing unless a canonical on-chain
 * SubdivisionVault child exists (``partial_land_sale_enabled``) — on Base Sepolia
 * it does not. NO fake whole-parcel fallback, NO demo fallback, NO silent
 * selection discard: the selection stays visible with a typed explanation.
 */
function RealSellWizard({
  open,
  sourceParcel,
  onClose,
  config,
}: {
  open: boolean;
  sourceParcel: SellSourceParcel;
  onClose: () => void;
  config: MarketplaceConfig;
}) {
  const t = useT();
  const { address, isConnected } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const publicClient = usePublicClient();
  const { writeContractAsync } = useWriteContract();
  const create = useCreateWholeParcelListing();

  const total = sourceParcel.w * sourceParcel.h;
  const isSingle = total === 1;

  const [step, setStep] = useState<RealStep>(isSingle ? "price" : "select");
  const [selected, setSelected] = useState<Set<CellKey>>(() =>
    isSingle ? new Set([keyOf(sourceParcel.x, sourceParcel.y)]) : new Set(),
  );
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [subStep, setSubStep] = useState<string | null>(null);

  const occ = useParcelOccupancy(sourceParcel.parcel_id, open && !isSingle);
  const lockedCells = useMemo(
    () => new Set((occ.data?.locked_cells ?? []).map(([x, y]) => keyOf(x, y))),
    [occ.data],
  );

  const allKeys = useMemo<Set<CellKey>>(() => {
    const s = new Set<CellKey>();
    for (let gy = sourceParcel.y; gy < sourceParcel.y + sourceParcel.h; gy++)
      for (let gx = sourceParcel.x; gx < sourceParcel.x + sourceParcel.w; gx++) s.add(keyOf(gx, gy));
    return s;
  }, [sourceParcel]);

  const cells = useMemo<[number, number][]>(
    () =>
      [...selected].map((k) => {
        const [x, y] = k.split(",").map(Number);
        return [x, y] as [number, number];
      }),
    [selected],
  );

  const selectedCount = selected.size;
  const isWhole = selectedCount > 0 && selectedCount === total;
  const isPartial = selectedCount > 0 && selectedCount < total;

  // Exact count of currently selected VALID cells — never counts locked/
  // ineligible/unselectable cells (defensive even if occupancy loads late).
  const selectedValidCount = useMemo(
    () => [...selected].filter((k) => !lockedCells.has(k)).length,
    [selected, lockedCells],
  );

  // Validate ONLY a partial selection (a full selection uses the canonical
  // whole-parcel path — the partial route rejects a full-parcel cover).
  const validation = useValidatePartial(sourceParcel.parcel_id, isPartial ? cells : []);
  const v = validation.data;

  const usdc = config.currencies.find((c) => c.symbol === "USDC");
  const sym = usdc?.symbol ?? "USDC";
  const usdcAddr = usdc?.address ?? null;
  const collection = config.land_collection;
  const verifyingContract = config.marketplace_address;

  const seller = (address ?? "").toLowerCase();
  const ownerMatch = !!seller && seller === String(sourceParcel.owner || "").toLowerCase();
  const configured = !!collection && !!verifyingContract && !!usdcAddr;

  const priceMicro = price ? Math.round(parseFloat(price) * 1_000_000) : 0;
  const floorMicro = v?.floor_micro ?? 0;
  const floorBinding = !!v?.floor_binding;
  const basisMicro = v?.basis_micro ?? 0;
  const partialSelectionValid = isPartial && !!v?.valid;

  // Price must clear the backend-authoritative floor when it is binding. For a
  // WHOLE selection the partial validation is not run (no floor) → any price > 0.
  const belowFloor = floorBinding && priceMicro > 0 && priceMicro < floorMicro;
  const priceValid = priceMicro > 0 && (!floorBinding || priceMicro >= floorMicro);

  // Compact row-major footprint of the selected cells (shown at review).
  const selectedFootprint = useMemo(
    () =>
      [...cells]
        .sort((a, b) => a[1] - b[1] || a[0] - b[0])
        .map(([x, y]) => `${x},${y}`)
        .join(" · "),
    [cells],
  );

  // A WHOLE-parcel sale requires EVERY parcel cell eligible: if any cell is
  // ineligible (a live marketplace lock OR canonical content occupancy) the
  // full-parcel path FAILS CLOSED — a full selection can never include an
  // ineligible cell. ``lockedCells`` is the composed union from parcel_occupancy.
  const wholeBlocked = lockedCells.size > 0;

  // A VALIDATED selection — WHOLE (all eligible cells) OR a valid PARTIAL subset —
  // may advance through price → review/preview. Fail-closed happens ONLY at the
  // final authorize/sign/list action, never at this area-selection gate.
  const canProceedFromSelect = (isWhole && !wholeBlocked) || partialSelectionValid;

  // WHOLE path: unchanged canonical whole-parcel authorization gate.
  const canAuthorize = configured && isConnected && ownerMatch && priceValid && isWhole && !wholeBlocked && !busy;
  // PARTIAL path: review + authorize are reachable; the action itself fails closed.
  const canAttemptPartial = isPartial && partialSelectionValid && priceValid && !busy;

  const gateReason = !configured
    ? t("marketplace.sell.wholeNotConfigured")
    : !isConnected
      ? t("marketplace.sell.wholeConnectRequired")
      : !ownerMatch
        ? t("marketplace.sell.wholeOwnerMismatch")
        : null;

  const reasonLabel = (r: string): string =>
    (
      {
        cells_outside_parent: t("marketplace.sell.reasonOutside"),
        not_contiguous: t("marketplace.sell.reasonNotContiguous"),
        full_parcel_use_whole_listing: t("marketplace.sell.reasonFull"),
        not_beneficial_owner: t("marketplace.sell.reasonNotOwner"),
        cells_overlap_existing: t("marketplace.sell.reasonOverlap"),
        cells_occupied_by_content: t("marketplace.sell.reasonOccupied"),
        no_cost_basis: t("marketplace.sell.reasonNoBasis"),
      } as Record<string, string>
    )[r] ?? r;

  const submit = async () => {
    // PARTIAL selection → REAL on-chain SubdivisionVault sale (BUG3, Claudio dec.):
    // pre-compute the EXACT split (fail-closed BEFORE any write), then
    // approve → deposit → split → reconcile → verify → sign → list the SELL child.
    if (isPartial) {
      if (!config.partial_land_sale_enabled || !config.subdivision_vault) {
        setErr(t("marketplace.sell.partialVaultRequired"));
        return;
      }
      if (!isConnected || !address || !ownerMatch || !collection || !verifyingContract || !usdcAddr || !publicClient) return;
      if (!priceValid) return;
      const vault = config.subdivision_vault as `0x${string}`;
      const chainId = config.chain_id;
      const seller0 = address as `0x${string}`;
      setErr(null);
      setBusy(true);
      try {
        // 0. PLAN — exact split; fail-closed BEFORE any irreversible on-chain write.
        setSubStep("plan");
        const plan = await marketplaceApi.planSubdivision({
          parcel_id: sourceParcel.parcel_id,
          rects: cells.map(([x, y]) => ({ x, y, w: 1, h: 1 })),
        });
        if (!plan.valid) {
          setErr((plan.reasons ?? []).map(reasonLabel).join(" · ") || t("marketplace.sell.partialVaultRequired"));
          return;
        }
        if (!plan.ok || plan.exceeds_limit) {
          setErr(t("marketplace.sell.subdivisionTooComplex", {
            total: String(plan.total_rects ?? 0), max: String(plan.max_rects_per_call),
          }));
          return;
        }
        const parentTokenId = BigInt(plan.parent_token_id ?? sourceParcel.parcel_id);

        // 1. APPROVE the vault on the parent collection (only if required).
        const approved = await publicClient.readContract({
          address: collection as `0x${string}`, abi: ERC721_APPROVAL_ABI,
          functionName: "isApprovedForAll", args: [seller0, vault],
        });
        if (!approved) {
          setSubStep("approve");
          const ah = await writeContractAsync({
            address: collection as `0x${string}`, abi: ERC721_APPROVAL_ABI,
            functionName: "setApprovalForAll", args: [vault, true], chainId,
          });
          await publicClient.waitForTransactionReceipt({ hash: ah });
        }

        // 2. DEPOSIT the parent → root child (fail-closed if already subdivided).
        const activeChildren = (await publicClient.readContract({
          address: vault, abi: SUBDIVISION_VAULT_ABI,
          functionName: "activeChildCount", args: [parentTokenId],
        })) as bigint;
        if (activeChildren > 0n) {
          setErr(t("marketplace.sell.subAlreadySubdivided"));
          return;
        }
        setSubStep("deposit");
        const dh = await writeContractAsync({
          address: vault, abi: SUBDIVISION_VAULT_ABI, functionName: "deposit",
          args: [parentTokenId], chainId,
        });
        const depRcpt = await publicClient.waitForTransactionReceipt({ hash: dh });
        const depLogs = parseEventLogs({ abi: SUBDIVISION_VAULT_ABI, logs: depRcpt.logs, eventName: "ParentDeposited" });
        const rootChildId = (depLogs[0]?.args as { rootChildId?: bigint } | undefined)?.rootChildId;
        if (rootChildId == null) { setErr(t("marketplace.sell.subDepositFailed")); return; }

        // 3. SPLIT root → [SELL child, REMAINDER child], both to the seller.
        setSubStep("split");
        const sh = await writeContractAsync({
          address: vault, abi: SUBDIVISION_VAULT_ABI, functionName: "split",
          args: [rootChildId, [seller0, seller0], [plan.sell_rects ?? [], plan.remainder_rects ?? []]],
          chainId,
        });
        const splitRcpt = await publicClient.waitForTransactionReceipt({ hash: sh });
        const splitLogs = parseEventLogs({ abi: SUBDIVISION_VAULT_ABI, logs: splitRcpt.logs, eventName: "ChildSplit" });
        const newChildIds = (splitLogs[0]?.args as { newChildIds?: readonly bigint[] } | undefined)?.newChildIds;
        const sellChildId = newChildIds?.[0];
        if (sellChildId == null) { setErr(t("marketplace.sell.subSplitFailed")); return; }

        // 4. RECONCILE the SELL child geometry from the on-chain vault view.
        setSubStep("reconcile");
        const reconciled = await marketplaceApi.reconcileSubdivisionChild(Number(sellChildId));
        if (reconciled.cell_count !== (plan.cell_count ?? cells.length)) {
          setErr(t("marketplace.sell.subGeometryMismatch"));
          return;
        }

        // 5. SIGN the EIP-712 order + CREATE the partial listing on the SELL child.
        setSubStep("sign");
        const nonce = Date.now();
        const expiry = Math.floor(Date.now() / 1000) + 30 * 86400;
        const typed = buildOrderTypedData({
          chainId, verifyingContract: verifyingContract as `0x${string}`,
          order: {
            seller: seller0, collection: vault, tokenId: sellChildId,
            currency: usdcAddr as `0x${string}`, price: BigInt(priceMicro),
            listingVersion: 0n, nonce: BigInt(nonce), expiryRealTs: BigInt(expiry),
          },
        });
        const signature = await signTypedDataAsync(typed);
        await marketplaceApi.createPartialListing({
          parcel_id: sourceParcel.parcel_id, child_token_id: Number(sellChildId),
          currency: usdcAddr, price_micro: priceMicro, listing_version: 0,
          nonce, expiry_real_ts: expiry, rects: plan.sell_rects ?? [],
          seller_signature: signature,
        });
        setStep("done");
      } catch (e) {
        setErr(e instanceof MarketplaceError ? e.message : e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
        setSubStep(null);
      }
      return;
    }
    if (!canAuthorize || !collection || !verifyingContract || !usdcAddr) return;
    setErr(null);
    setBusy(true);
    try {
      // Whole-parcel sales settle via TheGridMarketplace.fill(), which pulls the
      // LAND with safeTransferFrom(seller → buyer): the seller MUST first grant the
      // Marketplace transfer rights on THIS token, else every fill reverts closed.
      // Narrowest safe grant — a per-token approve (auto-cleared on transfer), never
      // a blanket operator approval.
      if (publicClient) {
        const mkt = verifyingContract as `0x${string}`;
        const tokenId = BigInt(sourceParcel.parcel_id);
        const [approvedAddr, approvedAll] = await Promise.all([
          publicClient.readContract({
            address: collection as `0x${string}`, abi: ERC721_APPROVAL_ABI,
            functionName: "getApproved", args: [tokenId],
          }),
          publicClient.readContract({
            address: collection as `0x${string}`, abi: ERC721_APPROVAL_ABI,
            functionName: "isApprovedForAll", args: [address as `0x${string}`, mkt],
          }),
        ]);
        const hasApproval =
          String(approvedAddr).toLowerCase() === mkt.toLowerCase() || approvedAll === true;
        if (!hasApproval) {
          setSubStep("approveCollection");
          const ah = await writeContractAsync({
            address: collection as `0x${string}`, abi: ERC721_APPROVAL_ABI,
            functionName: "approve", args: [mkt, tokenId], chainId: config.chain_id,
          });
          await publicClient.waitForTransactionReceipt({ hash: ah });
          const recheck = await publicClient.readContract({
            address: collection as `0x${string}`, abi: ERC721_APPROVAL_ABI,
            functionName: "getApproved", args: [tokenId],
          });
          if (String(recheck).toLowerCase() !== mkt.toLowerCase()) {
            setErr(t("marketplace.sell.nftApprovalFailed"));
            return;
          }
        }
      }
      setSubStep("sign");
      const nonce = Date.now();
      const expiry = Math.floor(Date.now() / 1000) + 30 * 86400;
      const typed = buildOrderTypedData({
        chainId: config.chain_id,
        verifyingContract: verifyingContract as `0x${string}`,
        order: {
          seller: address as `0x${string}`,
          collection: collection as `0x${string}`,
          tokenId: BigInt(sourceParcel.parcel_id),
          currency: usdcAddr as `0x${string}`,
          price: BigInt(priceMicro),
          listingVersion: 0n,
          nonce: BigInt(nonce),
          expiryRealTs: BigInt(expiry),
        },
      });
      const signature = await signTypedDataAsync(typed);
      await create.mutateAsync({
        parcel_id: sourceParcel.parcel_id,
        collection,
        token_id: sourceParcel.parcel_id,
        currency: usdcAddr,
        price_micro: priceMicro,
        listing_version: 0,
        nonce,
        expiry_real_ts: expiry,
        seller_signature: signature,
      });
      setStep("done");
    } catch (e) {
      setErr(e instanceof MarketplaceError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setSubStep(null);
    }
  };

  const goMine = () => {
    onClose();
    goMarketplace("mine");
  };

  const footer =
    step === "done" ? (
      <div className="flex justify-end gap-2">
        <MktButton variant="ghost" testId="sell-wizard-close" onClick={onClose}>
          {t("marketplace.sell.close")}
        </MktButton>
        <MktButton testId="sell-wizard-view-mine" onClick={goMine}>
          {t("marketplace.sell.viewMine")}
        </MktButton>
      </div>
    ) : (
      <div className="flex items-center justify-between gap-2">
        <MktButton
          variant="ghost"
          testId="sell-wizard-back"
          onClick={() =>
            step === "select" || (step === "price" && isSingle)
              ? onClose()
              : setStep(step === "review" ? "price" : "select")
          }
        >
          {step === "select" || (step === "price" && isSingle) ? t("marketplace.sell.close") : t("marketplace.sell.back")}
        </MktButton>
        {step === "select" && (
          <MktButton testId="sell-wizard-next-price" disabled={!canProceedFromSelect} onClick={() => setStep("price")}>
            {t("marketplace.sell.next")}
          </MktButton>
        )}
        {step === "price" && (
          <MktButton testId="sell-wizard-next-review" disabled={!priceValid} onClick={() => setStep("review")}>
            {t("marketplace.sell.next")}
          </MktButton>
        )}
        {step === "review" && (
          <MktButton
            variant="coral"
            testId="sell-wizard-authorize"
            disabled={isPartial ? !canAttemptPartial : !canAuthorize}
            onClick={submit}
          >
            {busy
              ? (subStep ? t(`marketplace.sell.sub_${subStep}`) : t("marketplace.sell.signing"))
              : t("marketplace.sell.signAndList")}
          </MktButton>
        )}
      </div>
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("marketplace.sell.wholeTitle")}
      subtitle={t("marketplace.sell.wholeSubtitle", { id: String(sourceParcel.parcel_id) })}
      footer={footer}
      testId="sell-land-wizard"
      size="lg"
    >
      <div className="mb-5 flex items-center gap-2" data-testid="sell-wizard-whole-badge">
        <Tag tone={isWhole ? "teal" : "muted"}>
          {isWhole ? t("marketplace.sell.wholeBadge") : t("marketplace.sell.partialBadge")}
        </Tag>
        <span className="font-mono text-[10.5px] text-text-muted">
          {sourceParcel.x},{sourceParcel.y} · {sourceParcel.w}×{sourceParcel.h} · {t("marketplace.sell.pickCells", { n: total })}
        </span>
        <span
          data-testid="sell-slot-counter"
          className="ml-auto font-mono text-[11px] font-semibold text-neon-teal tabular-nums"
        >
          {t("marketplace.sell.slotsSelected")}: {selectedValidCount} / {total}
          {isWhole ? ` — ${t("marketplace.sell.wholeBadge")}` : ""}
        </span>
      </div>

      {step === "select" && (
        <div className="space-y-4" data-testid="sell-step-select">
          <div>
            <h4 className="font-display text-sm text-text-primary">{t("marketplace.sell.realSelectTitle")}</h4>
            <p className="mt-1 font-mono text-[10.5px] leading-relaxed text-text-muted">
              {t("marketplace.sell.realSelectHint")}
            </p>
          </div>
          <div className="rounded-lg border border-border-divider bg-base/40 p-4">
            <SellMiniGrid parcel={sourceParcel} selected={selected} onChange={setSelected} lockedCells={lockedCells} />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span data-testid="sell-selected-count" className="font-mono text-[11px] text-text-secondary tabular-nums">
              {t("marketplace.sell.slotsSelected")}: {selectedValidCount} / {total}
              {isWhole ? ` — ${t("marketplace.sell.wholeBadge")}` : ""}
            </span>
            <div className="flex items-center gap-2">
              <MktButton variant="ghost" testId="sell-select-whole" onClick={() => setSelected(new Set(allKeys))} disabled={isWhole || wholeBlocked}>
                {t("marketplace.sell.selectWhole")}
              </MktButton>
              <MktButton variant="ghost" testId="sell-clear" onClick={() => setSelected(new Set())} disabled={!selectedCount}>
                {t("marketplace.sell.clearSelection")}
              </MktButton>
            </div>
          </div>
          {wholeBlocked && (
            <div data-testid="sell-whole-ineligible" className="rounded-lg border border-neon-coral/40 bg-neon-coral/10 px-4 py-3">
              <Tag tone="coral">{t("marketplace.sell.wholeIneligibleTitle")}</Tag>
              <p className="mt-2 font-mono text-[10.5px] leading-relaxed text-neon-coral">
                {t("marketplace.sell.wholeIneligibleBody")}
              </p>
            </div>
          )}
          {isWhole && (
            <div data-testid="sell-whole-selected" className="rounded-lg border border-neon-teal/40 bg-neon-teal/5 px-4 py-3 font-mono text-[10.5px] text-neon-teal">
              {t("marketplace.sell.wholeSelectedNote")}
            </div>
          )}
          {isPartial && (
            <SelectionSummary
              loading={validation.isFetching}
              valid={partialSelectionValid}
              reasons={(v?.reasons ?? []).map(reasonLabel)}
              basisMicro={basisMicro}
              floorMicro={floorMicro}
              floorBinding={floorBinding}
              sym={sym}
              t={t}
              show={true}
            />
          )}
        </div>
      )}

      {step === "price" && (
        <div className="space-y-5" data-testid="sell-step-price">
          <h4 className="font-display text-sm text-text-primary">{t("marketplace.sell.priceTitle")}</h4>
          {isPartial && (
            <div className="grid grid-cols-2 gap-4" data-testid="sell-partial-price-meta">
              <Field label={t("marketplace.sell.floorLabel")}>
                <Money amount={fmtMicro(floorMicro)} symbol={sym} className="text-sm text-neon-teal" testId="sell-price-floor" />
                <span className="ml-2">
                  <Tag tone={floorBinding ? "teal" : "muted"}>
                    {floorBinding ? t("marketplace.sell.floorBinding") : t("marketplace.sell.floorNotBinding")}
                  </Tag>
                </span>
              </Field>
              <Field label={t("marketplace.sell.basisLabel")}>
                <Money amount={fmtMicro(basisMicro)} symbol={sym} className="text-sm text-text-primary" />
              </Field>
            </div>
          )}
          <div>
            <label className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted" htmlFor="sell-price-input">
              {t("marketplace.sell.priceLabel")} ({sym})
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                id="sell-price-input"
                data-testid="sell-price-input"
                type="number"
                inputMode="decimal"
                min={0}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className={cn(
                  "h-11 flex-1 rounded-md border px-3 font-mono text-sm tabular-nums bg-subtle text-text-primary placeholder:text-text-muted [color-scheme:dark] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed",
                  belowFloor ? "border-state-danger focus:border-state-danger" : "border-border-divider focus:border-neon-teal/60",
                )}
                placeholder={isPartial && floorBinding ? microToExact(floorMicro) : "0.00"}
              />
              {isPartial && floorBinding && floorMicro > 0 && (
                <MktButton variant="ghost" testId="sell-price-set-floor" onClick={() => setPrice(microToExact(floorMicro))}>
                  min
                </MktButton>
              )}
            </div>
            {isPartial && floorBinding && (
              <p className="mt-2 font-mono text-[10.5px] text-text-muted">
                {t("marketplace.sell.priceHelpFloor", { floor: fmtMicro(floorMicro), sym })}
              </p>
            )}
            {isPartial && belowFloor && (
              <p data-testid="sell-below-floor" className="mt-2 font-mono text-[10.5px] text-state-danger">
                {t("marketplace.sell.belowFloor", { floor: fmtMicro(floorMicro), sym })}
              </p>
            )}
            {!isPartial && (
              <p className="mt-2 font-mono text-[10.5px] text-text-muted">
                {t("marketplace.sell.priceRealHelp", { sym })}
              </p>
            )}
          </div>
        </div>
      )}

      {step === "review" && (
        <div className="space-y-4" data-testid="sell-step-review">
          <div className="flex items-center gap-2">
            <h4 className="font-display text-sm text-text-primary">{t("marketplace.sell.reviewTitle")}</h4>
            {isPartial && (
              <Tag tone="coral" testId="sell-review-partial-tag">
                {t("marketplace.sell.partialBadge")}
              </Tag>
            )}
          </div>
          <div className="rounded-lg border border-border-divider divide-y divide-border-divider">
            <ReviewRow label={t("marketplace.sell.reviewCells")} value={isPartial ? `${selectedCount}` : `${total}`} />
            {isPartial && <ReviewRow label={t("marketplace.sell.reviewArea")} value={selectedFootprint} />}
            <ReviewRow label={t("marketplace.sell.reviewToken")} value={`#${sourceParcel.parcel_id}`} />
            {isPartial && <ReviewRow label={t("marketplace.sell.reviewFloor")} value={`${fmtMicro(floorMicro)} ${sym}`} />}
            <ReviewRow label={t("marketplace.sell.reviewPrice")} value={`${fmtMicro(priceMicro)} ${sym}`} />
          </div>
          {!isPartial && gateReason && (
            <p data-testid="sell-whole-gate" className="font-mono text-[10.5px] text-state-warning break-words">
              {gateReason}
            </p>
          )}
          {err && (
            <p data-testid="sell-create-error" className="font-mono text-[10.5px] text-state-danger break-words">
              {err}
            </p>
          )}
        </div>
      )}

      {step === "done" && (
        <div className="py-8 text-center space-y-3" data-testid="sell-step-done">
          <div className="mx-auto h-12 w-12 rounded-full border border-neon-teal/50 bg-neon-teal/10 inline-flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
              <path d="M4 10l4 4 8-9" stroke="var(--neon-teal, #37e0c8)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="font-display text-base text-text-primary">{t("marketplace.sell.realCreatedTitle")}</p>
          <p className="font-mono text-[11px] text-text-muted max-w-sm mx-auto leading-relaxed">
            {t("marketplace.sell.realCreatedBody")}
          </p>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// PREVIEW DEMO mode — isolated partial-sale flow (unchanged behaviour)
// ---------------------------------------------------------------------------
type Step = "select" | "price" | "review" | "done";

function DemoPartialWizard({
  open,
  sourceParcel,
  onClose,
  config,
}: {
  open: boolean;
  sourceParcel: SellSourceParcel;
  onClose: () => void;
  config: MarketplaceConfig;
}) {
  const t = useT();
  const [step, setStep] = useState<Step>("select");
  const [selected, setSelected] = useState<Set<CellKey>>(new Set());
  const [price, setPrice] = useState("");
  const create = useCreateDemoListing();

  const cells = useMemo<[number, number][]>(
    () =>
      [...selected].map((k) => {
        const [x, y] = k.split(",").map(Number);
        return [x, y] as [number, number];
      }),
    [selected],
  );

  const validation = useValidatePartial(sourceParcel.parcel_id, cells);
  const v = validation.data;

  const total = sourceParcel.w * sourceParcel.h;
  const usdc = config.currencies.find((c) => c.symbol === "USDC");
  const sym = usdc?.symbol ?? "USDC";
  const floorMicro = v?.floor_micro ?? 0;
  const floorBinding = !!v?.floor_binding;
  const basisMicro = v?.basis_micro ?? 0;
  const priceMicro = price ? Math.round(parseFloat(price) * 1_000_000) : 0;
  const belowFloor = floorBinding && priceMicro > 0 && priceMicro < floorMicro;
  const priceValid = priceMicro > 0 && (!floorBinding || priceMicro >= floorMicro);
  const selectionValid = !!v?.valid && selected.size > 0;

  const reasonLabel = (r: string): string =>
    (
      {
        cells_outside_parent: t("marketplace.sell.reasonOutside"),
        not_contiguous: t("marketplace.sell.reasonNotContiguous"),
        full_parcel_use_whole_listing: t("marketplace.sell.reasonFull"),
        not_beneficial_owner: t("marketplace.sell.reasonNotOwner"),
        cells_overlap_existing: t("marketplace.sell.reasonOverlap"),
        cells_occupied_by_content: t("marketplace.sell.reasonOccupied"),
        no_cost_basis: t("marketplace.sell.reasonNoBasis"),
      } as Record<string, string>
    )[r] ?? r;

  const submit = () => {
    if (!usdc?.address) return;
    create.mutate(
      {
        parcel_id: sourceParcel.parcel_id,
        currency: usdc.address,
        price_micro: priceMicro,
        nonce: Date.now(),
        expiry_real_ts: Math.floor(Date.now() / 1000) + 30 * 86400,
        rects: cells.map(([x, y]) => ({ x, y, w: 1, h: 1 })),
      },
      { onSuccess: () => setStep("done") },
    );
  };

  const goMine = () => {
    onClose();
    goMarketplace("mine");
  };

  const stepIndex = { select: 0, price: 1, review: 2, done: 3 }[step];

  const footer =
    step === "done" ? (
      <div className="flex justify-end gap-2">
        <MktButton variant="ghost" testId="sell-wizard-close" onClick={onClose}>
          {t("marketplace.sell.close")}
        </MktButton>
        <MktButton testId="sell-wizard-view-mine" onClick={goMine}>
          {t("marketplace.sell.viewMine")}
        </MktButton>
      </div>
    ) : (
      <div className="flex items-center justify-between gap-2">
        <MktButton
          variant="ghost"
          testId="sell-wizard-back"
          onClick={() => (step === "select" ? onClose() : setStep(step === "review" ? "price" : "select"))}
        >
          {step === "select" ? t("marketplace.sell.close") : t("marketplace.sell.back")}
        </MktButton>
        {step === "select" && (
          <MktButton testId="sell-wizard-next-price" disabled={!selectionValid} onClick={() => setStep("price")}>
            {t("marketplace.sell.next")}
          </MktButton>
        )}
        {step === "price" && (
          <MktButton testId="sell-wizard-next-review" disabled={!priceValid} onClick={() => setStep("review")}>
            {t("marketplace.sell.next")}
          </MktButton>
        )}
        {step === "review" && (
          <MktButton
            variant="coral"
            testId="sell-wizard-authorize"
            disabled={create.isPending || !selectionValid || !priceValid}
            onClick={submit}
          >
            {create.isPending ? t("marketplace.sell.creating") : t("marketplace.sell.authorizeDemo")}
          </MktButton>
        )}
      </div>
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("marketplace.sell.wizardTitle")}
      subtitle={t("marketplace.sell.wizardSubtitle", { id: sourceParcel.parcel_id })}
      footer={footer}
      testId="sell-land-wizard"
      size="lg"
    >
      <div className="mb-5 flex items-center gap-2" data-testid="sell-wizard-steps">
        {[t("marketplace.sell.stepSelect"), t("marketplace.sell.stepPrice"), t("marketplace.sell.stepReview")].map(
          (label, i) => (
            <div key={label} className="flex items-center gap-2">
              <span
                className={cn(
                  "font-mono text-[10px] tracking-[0.14em] uppercase px-2 h-6 inline-flex items-center rounded-sm border",
                  i <= stepIndex
                    ? "text-neon-teal border-neon-teal/50 bg-neon-teal/10"
                    : "text-text-muted border-border-divider",
                )}
              >
                {i + 1}. {label}
              </span>
              {i < 2 && <span className="h-px w-4 bg-border-divider" aria-hidden />}
            </div>
          ),
        )}
      </div>

      <div
        data-testid="sell-wizard-demo-disclosure"
        className="mb-5 rounded-lg border border-border-coral bg-neon-coral/5 px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <Tag tone="coral" testId="sell-wizard-demo-tag">
            {t("marketplace.sell.previewDemo")}
          </Tag>
          <span className="font-mono text-[10.5px] tracking-[0.1em] uppercase text-neon-coral">
            {t("marketplace.sell.demoDisclosureTitle")}
          </span>
        </div>
        <p className="mt-2 font-mono text-[10.5px] leading-relaxed text-text-muted">
          {t("marketplace.sell.demoDisclosureBody")}
        </p>
      </div>

      {step === "select" && (
        <div className="space-y-4" data-testid="sell-step-select">
          <div>
            <h4 className="font-display text-sm text-text-primary">{t("marketplace.sell.selectTitle")}</h4>
            <p className="mt-1 font-mono text-[10.5px] leading-relaxed text-text-muted">
              {t("marketplace.sell.selectHint")}
            </p>
          </div>
          <div className="rounded-lg border border-border-divider bg-base/40 p-4">
            <SellMiniGrid parcel={sourceParcel} selected={selected} onChange={setSelected} />
          </div>
          <div className="flex items-center justify-between">
            <span data-testid="sell-selected-count" className="font-mono text-[11px] text-text-secondary">
              {t("marketplace.sell.selectedCount", { n: selected.size, total })}
            </span>
            <MktButton variant="ghost" testId="sell-clear" onClick={() => setSelected(new Set())} disabled={!selected.size}>
              {t("marketplace.sell.clearSelection")}
            </MktButton>
          </div>
          <SelectionSummary
            loading={validation.isFetching}
            valid={selectionValid}
            reasons={(v?.reasons ?? []).map(reasonLabel)}
            basisMicro={basisMicro}
            floorMicro={floorMicro}
            floorBinding={floorBinding}
            sym={sym}
            t={t}
            show={selected.size > 0}
          />
        </div>
      )}

      {step === "price" && (
        <div className="space-y-5" data-testid="sell-step-price">
          <h4 className="font-display text-sm text-text-primary">{t("marketplace.sell.priceTitle")}</h4>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("marketplace.sell.floorLabel")}>
              <Money amount={fmtMicro(floorMicro)} symbol={sym} className="text-sm text-neon-teal" testId="sell-price-floor" />
              <span className="ml-2">
                <Tag tone={floorBinding ? "teal" : "muted"}>
                  {floorBinding ? t("marketplace.sell.floorBinding") : t("marketplace.sell.floorNotBinding")}
                </Tag>
              </span>
            </Field>
            <Field label={t("marketplace.sell.basisLabel")}>
              <Money amount={fmtMicro(basisMicro)} symbol={sym} className="text-sm text-text-primary" />
            </Field>
          </div>
          <div>
            <label className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted" htmlFor="sell-price-input">
              {t("marketplace.sell.priceLabel")} ({sym})
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                id="sell-price-input"
                data-testid="sell-price-input"
                type="number"
                inputMode="decimal"
                min={0}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className={cn(
                  "h-11 flex-1 rounded-md border px-3 font-mono text-sm tabular-nums bg-subtle text-text-primary placeholder:text-text-muted [color-scheme:dark] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed",
                  belowFloor ? "border-state-danger focus:border-state-danger" : "border-border-divider focus:border-neon-teal/60",
                )}
                placeholder={floorBinding ? microToExact(floorMicro) : "0.00"}
              />
              {floorBinding && floorMicro > 0 && (
                <MktButton variant="ghost" testId="sell-price-set-floor" onClick={() => setPrice(microToExact(floorMicro))}>
                  min
                </MktButton>
              )}
            </div>
            {floorBinding && (
              <p className="mt-2 font-mono text-[10.5px] text-text-muted">
                {t("marketplace.sell.priceHelpFloor", { floor: fmtMicro(floorMicro), sym })}
              </p>
            )}
            {belowFloor && (
              <p data-testid="sell-below-floor" className="mt-2 font-mono text-[10.5px] text-state-danger">
                {t("marketplace.sell.belowFloor", { floor: fmtMicro(floorMicro), sym })}
              </p>
            )}
          </div>
        </div>
      )}

      {step === "review" && (
        <div className="space-y-4" data-testid="sell-step-review">
          <h4 className="font-display text-sm text-text-primary">{t("marketplace.sell.reviewTitle")}</h4>
          <div className="rounded-lg border border-border-divider divide-y divide-border-divider">
            <ReviewRow label={t("marketplace.sell.reviewCells")} value={`${selected.size}`} />
            <ReviewRow label={t("marketplace.sell.reviewFloor")} value={`${fmtMicro(floorMicro)} ${sym}`} />
            <ReviewRow label={t("marketplace.sell.reviewPrice")} value={`${fmtMicro(priceMicro)} ${sym}`} />
          </div>
          {create.isError && (
            <p data-testid="sell-create-error" className="font-mono text-[10.5px] text-state-danger break-words">
              {create.error instanceof MarketplaceError ? create.error.message : String(create.error)}
            </p>
          )}
        </div>
      )}

      {step === "done" && (
        <div className="py-8 text-center space-y-3" data-testid="sell-step-done">
          <div className="mx-auto h-12 w-12 rounded-full border border-neon-teal/50 bg-neon-teal/10 inline-flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
              <path d="M4 10l4 4 8-9" stroke="var(--neon-teal, #37e0c8)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="font-display text-base text-text-primary">{t("marketplace.sell.createdTitle")}</p>
          <p className="font-mono text-[11px] text-text-muted max-w-sm mx-auto leading-relaxed">
            {t("marketplace.sell.createdBody")}
          </p>
          <div className="pt-1">
            <Tag tone="coral">{t("marketplace.sell.previewDemo")}</Tag>
          </div>
        </div>
      )}
    </Modal>
  );
}

function SelectionSummary({
  loading,
  valid,
  reasons,
  basisMicro,
  floorMicro,
  floorBinding,
  sym,
  t,
  show,
}: {
  loading: boolean;
  valid: boolean;
  reasons: string[];
  basisMicro: number;
  floorMicro: number;
  floorBinding: boolean;
  sym: string;
  t: (k: string, v?: Record<string, string | number>) => string;
  show: boolean;
}) {
  if (!show) return null;
  return (
    <div
      data-testid="sell-selection-summary"
      className={cn(
        "rounded-lg border px-4 py-3",
        valid ? "border-neon-teal/40 bg-neon-teal/5" : "border-state-warning/40 bg-state-warning/5",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          data-testid="sell-validation-state"
          data-valid={valid ? "true" : "false"}
          className={cn("font-mono text-[10.5px] tracking-[0.14em] uppercase", valid ? "text-neon-teal" : "text-state-warning")}
        >
          {loading ? t("marketplace.sell.valLoading") : valid ? t("marketplace.sell.valValid") : t("marketplace.sell.valInvalid")}
        </span>
        {floorMicro > 0 && (
          <span className="font-mono text-[10.5px] text-text-secondary">
            {t("marketplace.sell.floorLabel")}: {fmtMicro(floorMicro)} {sym}
            {!floorBinding && ` · ${t("marketplace.sell.floorNotBinding")}`}
          </span>
        )}
      </div>
      {basisMicro > 0 && (
        <p className="mt-1 font-mono text-[10px] text-text-muted">
          {t("marketplace.sell.basisLabel")}: {fmtMicro(basisMicro)} {sym}
        </p>
      )}
      {!valid && reasons.length > 0 && (
        <ul className="mt-2 space-y-1">
          {reasons.map((r) => (
            <li key={r} className="font-mono text-[10px] text-state-warning">
              · {r}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted">{label}</span>
      <span className="flex items-center">{children}</span>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-text-muted">{label}</span>
      <span className="font-display text-sm text-text-primary tabular-nums">{value}</span>
    </div>
  );
}
